/**
 * InstallPrompt — driver-focused PWA install banner.
 *
 * Listens to the browser's `beforeinstallprompt` event and surfaces a compact
 * banner on `/driver/*` and `/portal/*` routes (mobile-oriented experiences
 * benefit most from installing). The banner is dismissible per-session and
 * remembers a "don't show for 14 days" preference in localStorage.
 *
 * Also shows an update banner when a new service worker is waiting.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, X, RefreshCw, Smartphone } from "lucide-react";
import { skipWaitingAndReload } from "@/registerSW";
import { useLandingCMS } from "@/hooks/useLandingCMS";

const DISMISS_KEY = "pwa-install-dismissed-at";
const DISMISS_DAYS = 14;

function panelForRoute(pathname) {
  if (pathname.startsWith("/portal")) return "customer";
  if (pathname.startsWith("/driver")) return "driver";
  if (pathname === "/login" || pathname === "/register") return "auth";
  return null;
}

function isRecentlyDismissed() {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (!at) return false;
    const ageMs = Date.now() - at;
    return ageMs < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function InstallPrompt() {
  const location = useLocation();
  const { brand } = useLandingCMS();
  const masterEnabled = brand?.pwa_install_prompt_enabled !== false;
  const panel = panelForRoute(location.pathname);
  const perPanelKey = panel && `pwa_install_prompt_${panel}`;
  const perPanelEnabled = !panel || brand?.[perPanelKey] !== false;
  const cmsEnabled = masterEnabled && perPanelEnabled;
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [updateWorker, setUpdateWorker] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isStandalone()) return undefined;
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onUpdate = (e) => setUpdateWorker(e.detail?.worker || null);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("sw-update-ready", onUpdate);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("sw-update-ready", onUpdate);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (choice?.outcome !== "accepted") dismiss();
  };

  // Update banner takes precedence — showing "there's a newer version" beats
  // "install the app" for a returning user.
  if (updateWorker) {
    return (
      <div
        className="fixed inset-x-0 bottom-4 z-[60] mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-primary/30 bg-primary px-4 py-3 text-white shadow-2xl md:right-4 md:bottom-4 md:left-auto md:mx-0"
        data-testid="pwa-update-banner"
      >
        <RefreshCw size={18} className="shrink-0" />
        <div className="min-w-0 flex-1 text-sm">
          <div className="font-bold leading-tight">Update available</div>
          <div className="text-[11px] opacity-90">A newer version is ready — reload to apply.</div>
        </div>
        <button
          type="button"
          onClick={() => skipWaitingAndReload(updateWorker)}
          className="rounded-lg bg-white/15 px-3 py-1.5 text-xs font-bold hover:bg-white/25"
          data-testid="pwa-update-reload"
        >
          Reload
        </button>
      </div>
    );
  }

  if (dismissed || !deferredPrompt) return null;
  if (!cmsEnabled) return null;
  if (isRecentlyDismissed()) return null;
  if (!panel) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-4 z-[60] mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-primary/30 bg-white px-4 py-3 shadow-2xl md:right-4 md:bottom-4 md:left-auto md:mx-0 dark:bg-slate-900"
      data-testid="pwa-install-banner"
    >
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
        <Smartphone size={20} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold leading-tight">Install FuelExpress</div>
        <div className="text-[11px] text-muted-foreground">Faster launch · offline access · home-screen icon.</div>
      </div>
      <button
        type="button"
        onClick={install}
        className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:opacity-90"
        data-testid="pwa-install-cta"
      >
        <Download size={12} /> Install
      </button>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-muted"
        data-testid="pwa-install-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
